import {
  ACESFilmicToneMapping,
  Color,
  DirectionalLight,
  Fog,
  HemisphereLight,
  PerspectiveCamera,
  Scene,
  WebGLRenderer,
} from 'three';
import { applyPose, sampleKeyframes } from './animator';
import { planToken, type MotionPlan } from './dictionary';
import { buildRig, type Rig } from './rig';
import { REST, THINKING } from './signs';
import type { MotionProvenance } from './types';

export { availableLetters, describeProvenance, planToken } from './dictionary';
export type { MotionPlan, MotionSegment } from './dictionary';
export type { MotionProvenance } from './types';

/** Matches the ink stage the avatar sits in. */
const BACKGROUND = new Color('#201e1d');

function supportsWebGL(): boolean {
  try {
    const probe = document.createElement('canvas');
    return Boolean(probe.getContext('webgl2') ?? probe.getContext('webgl'));
  } catch {
    return false;
  }
}

export interface SigningAvatarOptions {
  canvas: HTMLCanvasElement;
  /** Called whenever the shown sign changes, with what can honestly be claimed. */
  onProvenance?(provenance: MotionProvenance, segment: string): void;
}

/**
 * A 3D signing avatar driven per gloss token.
 *
 * The app owns the clock — it decides which token is current and how long each
 * one lasts — and this drives the body to match, so the stage, gloss strip,
 * ticks and counter never drift apart. Swapping this for mocap or recorded
 * clips means replacing the pose source, not the timing contract.
 */
export class SigningAvatar {
  private readonly renderer: WebGLRenderer;
  private readonly scene: Scene;
  private readonly camera: PerspectiveCamera;
  private readonly rig: Rig;
  private readonly canvas: HTMLCanvasElement;
  private readonly onProvenance: SigningAvatarOptions['onProvenance'];
  private readonly observer: ResizeObserver | null = null;

  private plan: MotionPlan = planToken('');
  private segmentIndex = -1;
  private durationMs = 760;
  private elapsed = 0;
  private playing = false;
  private frame = 0;
  private lastTime = 0;
  private disposed = false;

  /** Returns null when WebGL is unavailable, so callers can fall back. */
  static create(options: SigningAvatarOptions): SigningAvatar | null {
    // No WebGL is an expected environment, not a fault — fall back quietly.
    if (!supportsWebGL()) return null;
    try {
      return new SigningAvatar(options);
    } catch (error) {
      console.warn('[signpost] 3D avatar could not start; falling back to the type stage.', error);
      return null;
    }
  }

  private constructor({ canvas, onProvenance }: SigningAvatarOptions) {
    this.canvas = canvas;
    this.onProvenance = onProvenance;

    this.renderer = new WebGLRenderer({ canvas, antialias: true, alpha: false });
    this.renderer.setPixelRatio(Math.min(2, globalThis.devicePixelRatio || 1));
    this.renderer.toneMapping = ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.1;

    this.scene = new Scene();
    this.scene.background = BACKGROUND;
    this.scene.fog = new Fog(BACKGROUND, 1.9, 3.6);

    // Framed on the signing space — roughly waist to above the head — so
    // handshapes get as many pixels as the stage can give them.
    this.camera = new PerspectiveCamera(30, 1, 0.1, 20);
    this.camera.position.set(0, 1.46, 1.62);
    this.camera.lookAt(0, 1.4, 0.06);

    this.scene.add(new HemisphereLight(0xf1ece7, 0x1a1817, 0.95));

    // Keyed from above and to the signer's right, which is where a face reads
    // best; a soft fill keeps the weak hand from falling into shadow, since
    // the alphabet is built on what the two hands do together.
    const key = new DirectionalLight(0xfff4e8, 1.45);
    key.position.set(1.1, 2.4, 2.2);
    this.scene.add(key);

    const fill = new DirectionalLight(0xdce6f0, 0.42);
    fill.position.set(-1.5, 0.9, 1.6);
    this.scene.add(fill);

    // A warm rim separates the figure from the ink ground.
    const rim = new DirectionalLight(0xec3013, 0.38);
    rim.position.set(-1.6, 1.4, -1.2);
    this.scene.add(rim);

    this.rig = buildRig();
    this.scene.add(this.rig.root);

    applyPose(this.rig, sampleKeyframes(REST.keyframes, 0));

    if (typeof ResizeObserver !== 'undefined') {
      this.observer = new ResizeObserver(() => this.resize());
      this.observer.observe(canvas);
    }
    this.resize();
  }

  /**
   * Show a gloss token. Restarting the same token replays it, which is what a
   * replay or a seek back to the same sign should do.
   *
   * Paused, the avatar jumps to the sign's hold rather than its preparation —
   * pinning a token should show the form the sign actually makes.
   */
  setToken(token: string, durationMs: number, playing: boolean): MotionProvenance {
    this.plan = planToken(token);
    this.durationMs = Math.max(120, durationMs);
    this.elapsed = playing ? 0 : this.durationMs;
    this.segmentIndex = -1;
    this.update(0);
    return this.plan.provenance;
  }

  setPlaying(playing: boolean): void {
    if (this.playing === playing) return;
    this.playing = playing;
    if (playing) {
      this.lastTime = 0;
      this.loop();
    } else {
      this.stopLoop();
      this.render();
    }
  }

  /** Held while the interpreter is working, so the stage is never dead. */
  setThinking(thinking: boolean): void {
    if (!thinking) return;
    this.plan = { segments: [{ label: 'THINKING', sign: THINKING }], provenance: { kind: 'none' }, playable: 1 };
    this.durationMs = 1400;
    this.elapsed = 0;
    this.segmentIndex = -1;
    this.update(0);
  }

  resize(): void {
    const width = this.canvas.clientWidth || 1;
    const height = this.canvas.clientHeight || 1;
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.render();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.stopLoop();
    this.observer?.disconnect();
    this.scene.traverse((node) => {
      const mesh = node as { geometry?: { dispose(): void }; material?: { dispose(): void } };
      mesh.geometry?.dispose();
      mesh.material?.dispose();
    });
    this.renderer.dispose();
  }

  private loop = (): void => {
    if (this.disposed || !this.playing) return;
    this.frame = requestAnimationFrame(this.loop);

    const now = performance.now();
    const delta = this.lastTime ? now - this.lastTime : 0;
    this.lastTime = now;

    this.update(delta);
  };

  private stopLoop(): void {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = 0;
  }

  private update(deltaMs: number): void {
    const segments = this.plan.segments;
    if (!segments.length) return;

    // A token's time is split evenly across its segments, so a fingerspelled
    // word and a single sign both finish exactly when the token does.
    const perSegment = this.durationMs / segments.length;
    this.elapsed = Math.min(this.durationMs, this.elapsed + deltaMs);

    const index = Math.min(segments.length - 1, Math.floor(this.elapsed / perSegment));
    const segment = segments[index]!;

    if (index !== this.segmentIndex) {
      this.segmentIndex = index;
      this.onProvenance?.(this.plan.provenance, segment.label);
    }

    const within = (this.elapsed - index * perSegment) / perSegment;
    // A segment with no sourced motion rests rather than inventing a shape.
    const keyframes = (segment.sign ?? REST).keyframes;
    applyPose(this.rig, sampleKeyframes(keyframes, within));
    this.render();
  }

  private render(): void {
    if (!this.disposed) this.renderer.render(this.scene, this.camera);
  }
}
